<script setup lang="ts">
import { computed } from "vue";

import { eventActions } from "@/action/eventActions";
import { timerAdded } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { useArtStore } from "@/store/art";
import { useBoardStore } from "@/store/board";
import { useTimerStore } from "@/store/timer";

import EditTimer from "./EditTimer.vue";

defineEmits<{ close: [] }>();

const boardTimers = computed(() => useTimerStore().boardTimers);
const artTimers = computed(() => useTimerStore().artTimers);

function addBoardTimer() {
  trackEvent(timerAdded("board"));
  addTimer({ boardId: useBoardStore().currentBoard().id });
}

function addArtTimer() {
  trackEvent(timerAdded("art"));
  addTimer({ artId: useArtStore().current.id });
}

function addTimer(target: { boardId?: string; artId?: string }) {
  eventActions.createTimer("sidebar-menu", target);
}
</script>

<template>
  <SidePanelContent
    v-focus-trap="{ setReturnFocus: '#facilitation-menu-trigger' }"
    v-autofocus
    :title="$t('timer.title')"
    :aria-label="$t('label.timerPanel')"
  >
    <div class="edit-timers">
      <div class="scope">{{ $t("timer.thisBoard") }}</div>
      <EditTimer v-for="timer in boardTimers" :key="timer.id" :timer />
      <BaseButton
        v-if="boardTimers.length < 1"
        variant="ghost"
        color="primary"
        icon-before="system/plus"
        class="add-timer"
        :aria-label="$t('label.timerPanel.addTeamTimer')"
        @click="addBoardTimer"
      >
        {{ $t("timer.add") }}
      </BaseButton>

      <div class="scope">{{ $t("timer.thisArt") }}</div>
      <EditTimer v-for="timer in artTimers" :key="timer.id" :timer />
      <BaseButton
        v-if="artTimers.length < 1"
        variant="ghost"
        color="primary"
        icon-before="system/plus"
        class="add-timer"
        :aria-label="$t('label.timerPanel.addArtTimer')"
        @click="addArtTimer"
      >
        {{ $t("timer.add") }}
      </BaseButton>
    </div>
  </SidePanelContent>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/variables/colors";

.edit-timers {
  margin-bottom: 2em;

  .scope {
    padding: 2em 2em 1em;
    font-weight: font.$weight-bold;
    color: colors.$text;
    font-size: 14px;
  }

  .add-timer {
    margin-left: 16px;
  }
}
</style>
